import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hybridDashboardService } from '../services/hybridDashboardService';
import { AppUser } from '../types';
import { Timestamp } from 'firebase/firestore';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'firebase' | 'hybrid'>('supabase');

  // Sayfalama
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 10;
  
  // 📸 Yüklenemeyen görselleri takip et
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Tarih formatla
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Bilinmiyor';
    
    try {
      let date;
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      return 'Bilinmiyor';
    }
  };

  // Kullanıcı durumu etiketini al (basit versiyon)
  const getUserStatus = (user: AppUser) => {
    if (!user.isActive) return 'Pasif';
    return 'Aktif';
  };

  // Kullanıcı durumunu değiştir
  const handleUserStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      setProcessingUserId(userId);
      
      // 🔄 HYBRID: Kullanıcı durumu güncelleme
      const result = await hybridDashboardService.updateUserStatus(userId, !currentStatus);
      
      if (result.success) {
        // Kullanıcı listesini güncelle
        setUsers(prev => 
          prev.map(user => 
            user.id === userId 
              ? { ...user, isActive: !currentStatus }
              : user
          )
        );

        console.log(`✅ HYBRID: Kullanıcı ${userId} durumu güncellendi (${result.source}): ${!currentStatus ? 'Aktif' : 'Pasif'}`);
      } else {
        throw new Error('Kullanıcı durumu güncellenemedi');
      }
    } catch (error) {
      console.error('❌ HYBRID: Kullanıcı durumu güncellenirken hata:', error);
      alert('Kullanıcı durumu güncellenirken hata oluştu!');
    } finally {
      setProcessingUserId(null);
    }
  };

  // Filtrelenmiş kullanıcıları al
  const getFilteredUsers = () => {
    return users
      .filter(user => {
        // Arama metnini temizle ve küçük harfe çevir
        const searchTerm = searchQuery.trim().toLowerCase();
        
        // Arama filtresi
        let matchesSearch = true;
        if (searchTerm) {
          // Gelişmiş arama - isim, e-mail ve ID ile
          const nameMatch = user.name?.toLowerCase().includes(searchTerm);
          const emailMatch = user.email?.toLowerCase().includes(searchTerm);
          const idMatch = user.id?.toLowerCase().includes(searchTerm);
          
          matchesSearch = nameMatch || emailMatch || idMatch;
        }
        
        // Durum filtresi
        const matchesStatus = 
          statusFilter === 'all' ||
          (statusFilter === 'active' && user.isActive) ||
          (statusFilter === 'inactive' && !user.isActive);

        // Cinsiyet filtresi - kullanıcının Firebase'deki gender alanını kontrol et
        let matchesGender = true;
        if (genderFilter !== 'all') {
          // Firebase'den gelen cinsiyet verisini kontrol et
          const userGender = (user as any).gender || null;
          
          if (genderFilter === 'male') {
            matchesGender = userGender === 'Erkek' || userGender === 'male';
          } else if (genderFilter === 'female') {
            matchesGender = userGender === 'Kadın' || userGender === 'female';
          } else if (genderFilter === 'other') {
            matchesGender = userGender === 'Diğer' || userGender === 'other' || 
                          (!userGender || (userGender !== 'Erkek' && userGender !== 'Kadın' && 
                           userGender !== 'male' && userGender !== 'female'));
          }
        }

        return matchesSearch && matchesStatus && matchesGender;
      })
      .sort((a, b) => {
        // En yeni üye en üste - createdAt'e göre descending sıralama
        const dateA = a.createdAt;
        const dateB = b.createdAt;
        
        // Eğer tarih yoksa en aşağıya koy
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // Timestamp objeleri için karşılaştırma
        let timeA, timeB;
        
        try {
          if (dateA.toDate) {
            timeA = dateA.toDate().getTime();
          } else {
            timeA = new Date(dateA).getTime();
          }
        } catch (error) {
          timeA = 0;
        }
        
        try {
          if (dateB.toDate) {
            timeB = dateB.toDate().getTime();
          } else {
            timeB = new Date(dateB).getTime();
          }
        } catch (error) {
          timeB = 0;
        }
        
        // Büyükten küçüğe sırala (en yeni en üstte)
        return timeB - timeA;
      });
  };

  // Kullanıcıları yükle
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 HYBRID: Kullanıcılar yükleniyor...');

        // 🔄 HYBRID: Tüm kullanıcıları getir
        const result = await hybridDashboardService.getAllUsersWithPagination({
          limit: 1000, // Tüm kullanıcıları getir, filtreleme frontend'de yapılacak
          offset: 0,
          searchQuery: '',
          statusFilter: 'all',
          genderFilter: 'all'
        });

        setUsers(result.users);
        setTotalUsers(result.total);
        setDataSource(result.source);

        console.log(`✅ HYBRID: ${result.users.length} kullanıcı yüklendi (${result.source}), Toplam: ${result.total}`);

      } catch (err) {
        console.error('❌ HYBRID: Kullanıcı yükleme hatası:', err);
        setError('Kullanıcılar yüklenemedi. Lütfen sayfayı yenileyin.');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
    
    // 🚀 PERFORMANS: Otomatik yenileme kaldırıldı - manuel yenileme tercih edilir
  }, []);

  const filteredUsers = getFilteredUsers();
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #F78D93',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p style={{ color: '#666' }}>Kullanıcılar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        backgroundColor: '#f8d7da',
        borderRadius: '8px',
        color: '#721c24'
      }}>
        <h3>⚠️ Hata</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#F78D93',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Sayfayı Yenile
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* 🔄 HYBRID System Status Banner */}
      <div style={{
        backgroundColor: dataSource === 'supabase' ? '#d4edda' : dataSource === 'firebase' ? '#f8d7da' : '#fff3cd',
        border: `1px solid ${dataSource === 'supabase' ? '#c3e6cb' : dataSource === 'firebase' ? '#f5c6cb' : '#ffeaa7'}`,
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            fontSize: '1.5rem',
          }}>
            {dataSource === 'supabase' && '🟢'}
            {dataSource === 'firebase' && '🔴'}
            {dataSource === 'hybrid' && '🟡'}
          </div>
          <div>
            <h4 style={{ 
              margin: 0, 
              color: dataSource === 'supabase' ? '#155724' : dataSource === 'firebase' ? '#721c24' : '#856404',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              Hybrid Database System - {dataSource === 'supabase' ? 'Supabase Primary' : dataSource === 'firebase' ? 'Firebase Fallback' : 'Mixed Sources'}
            </h4>
            <p style={{ 
              margin: 0, 
              color: dataSource === 'supabase' ? '#155724' : dataSource === 'firebase' ? '#721c24' : '#856404',
              fontSize: '0.9rem'
            }}>
              {dataSource === 'supabase' && 'Kullanıcı verileri Supabase\'den başarıyla yüklendi'}
              {dataSource === 'firebase' && 'Supabase erişilemiyor, Firebase fallback aktif'}
              {dataSource === 'hybrid' && 'Karışık veri kaynakları aktif'}
            </p>
          </div>
        </div>
        <div style={{
          backgroundColor: dataSource === 'supabase' ? '#28a745' : dataSource === 'firebase' ? '#dc3545' : '#ffc107',
          color: dataSource === 'hybrid' ? '#212529' : 'white',
          padding: '0.5rem 1rem',
          borderRadius: '20px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}>
          {dataSource === 'supabase' && 'OPTIMAL'}
          {dataSource === 'firebase' && 'FALLBACK'}
          {dataSource === 'hybrid' && 'MIXED'}
        </div>
      </div>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ 
            color: '#2c3e50', 
            margin: '0 0 0.5rem 0',
            fontSize: '1.8rem',
            fontWeight: '600'
          }}>
            👥 Kullanıcı Yönetimi
          </h1>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            Toplam {totalUsers} kullanıcı bulundu
            <span style={{ 
              fontSize: '0.8rem', 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              marginLeft: '0.5rem',
              backgroundColor: dataSource === 'supabase' ? '#d4edda' : dataSource === 'firebase' ? '#f8d7da' : '#fff3cd',
              padding: '0.25rem 0.5rem',
              borderRadius: '12px',
              border: `1px solid ${dataSource === 'supabase' ? '#c3e6cb' : dataSource === 'firebase' ? '#f5c6cb' : '#ffeaa7'}`
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase'}
              {dataSource === 'hybrid' && '🟡 Hybrid'}
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button 
            onClick={async () => {
              console.log('🔧 USERS TEST: Supabase kullanıcı listesi test ediliyor...');
              try {
                const result = await hybridDashboardService.getAllUsersWithPagination({
                  limit: 10,
                  offset: 0
                });
                console.log('✅ USERS TEST: Başarılı!', result);
                alert(`✅ Test Başarılı!\nKullanıcı sayısı: ${result.users.length}\nToplam: ${result.total}\nKaynak: ${result.source}`);
              } catch (error) {
                console.error('❌ USERS TEST: Hata!', error);
                alert(`❌ Test Hatası!\n${(error as any)?.message || error}`);
              }
            }}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            🧪 Kullanıcı Test
          </button>
          <button 
            onClick={async () => {
              console.log('🔄 MANUAL REFRESH: Kullanıcı listesi manuel olarak yenileniyor...');
              setLoading(true);
              try {
                const result = await hybridDashboardService.getAllUsersWithPagination({
                  limit: 1000,
                  offset: 0,
                  searchQuery: '',
                  statusFilter: 'all',
                  genderFilter: 'all'
                });
                setUsers(result.users);
                setTotalUsers(result.total);
                setDataSource(result.source);
                console.log(`✅ MANUAL REFRESH: ${result.users.length} kullanıcı yenilendi`);
              } catch (error) {
                console.error('❌ MANUAL REFRESH: Hata!', error);
                setError('Yenileme sırasında hata oluştu');
              } finally {
                setLoading(false);
              }
            }}
            style={{
              backgroundColor: '#F78D93',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            🔄 Yenile
          </button>
        </div>
      </div>

      {/* Gender Filter Tabs */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {[
            { key: 'all', label: 'Tümü', icon: '👥', color: '#6c757d' },
            { key: 'female', label: 'Kadın', icon: '👩', color: '#e91e63' },
            { key: 'male', label: 'Erkek', icon: '👨', color: '#2196f3' },
            { key: 'other', label: 'Diğer', icon: '🏳️‍⚧️', color: '#9c27b0' }
          ].map((tab) => {
            const isActive = genderFilter === tab.key;
            const userCount = tab.key === 'all' 
              ? users.length 
              : users.filter(user => {
                  const userGender = (user as any).gender || null;
                  if (tab.key === 'male') {
                    return userGender === 'Erkek' || userGender === 'male';
                  } else if (tab.key === 'female') {
                    return userGender === 'Kadın' || userGender === 'female';
                  } else if (tab.key === 'other') {
                    return userGender === 'Diğer' || userGender === 'other' || 
                          (!userGender || (userGender !== 'Erkek' && userGender !== 'Kadın' && 
                           userGender !== 'male' && userGender !== 'female'));
                  }
                  return false;
                }).length;

            return (
              <button
                key={tab.key}
                onClick={() => {
                  setGenderFilter(tab.key);
                  setCurrentPage(1); // Sayfa sıfırla
                }}
                style={{
                  flex: 1,
                  padding: '1rem 1.5rem',
                  border: 'none',
                  backgroundColor: isActive ? tab.color : 'transparent',
                  color: isActive ? 'white' : tab.color,
                  borderBottom: isActive ? 'none' : `3px solid transparent`,
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: isActive ? '600' : '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s ease',
                  borderTop: isActive ? `3px solid ${tab.color}` : '3px solid transparent'
                }}
                onMouseOver={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = `${tab.color}15`;
                  }
                }}
                onMouseOut={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
                <span>{tab.label}</span>
                <span style={{
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : `${tab.color}20`,
                  color: isActive ? 'white' : tab.color,
                  padding: '0.25rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  minWidth: '24px',
                  textAlign: 'center'
                }}>
                  {userCount}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="🔍 Kullanıcı ara (isim, e-mail veya ID ile)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.9rem',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <select 
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.75rem',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '0.9rem',
            minWidth: '120px'
          }}
        >
          <option value="all">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="inactive">Pasif</option>
        </select>
      </div>

      {/* Users Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}>
          <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.1rem' }}>
            Kullanıcı Listesi ({filteredUsers.length} kullanıcı)
            <span style={{ 
              fontSize: '0.8rem', 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontWeight: 'normal',
              marginLeft: '0.5rem',
              backgroundColor: dataSource === 'supabase' ? '#d4edda' : dataSource === 'firebase' ? '#f8d7da' : '#fff3cd',
              padding: '0.25rem 0.5rem',
              borderRadius: '12px',
              border: `1px solid ${dataSource === 'supabase' ? '#c3e6cb' : dataSource === 'firebase' ? '#f5c6cb' : '#ffeaa7'}`
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase'}
              {dataSource === 'hybrid' && '🟡 Hybrid'}
            </span>
            <span style={{ 
              fontSize: '0.8rem', 
              color: '#27ae60', 
              fontWeight: 'normal',
              marginLeft: '0.5rem',
              backgroundColor: '#d4edda',
              padding: '0.25rem 0.5rem',
              borderRadius: '12px'
            }}>
              📅 En yeni → En eski
            </span>
            {searchQuery.trim() && (
              <span style={{ 
                fontSize: '0.9rem', 
                color: '#F78D93', 
                fontWeight: 'normal',
                marginLeft: '0.5rem'
              }}>
                - "{searchQuery.trim()}" araması
              </span>
            )}
          </h3>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {paginatedUsers.length === 0 ? (
            <div style={{ 
              padding: '3rem', 
              textAlign: 'center', 
              color: '#666' 
            }}>
              {(searchQuery !== '' || statusFilter !== 'all' || genderFilter !== 'all') ? (
                <div>
                  <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>🔍</p>
                  <p>Arama kriterlerinize uygun kullanıcı bulunamadı.</p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                      setGenderFilter('all');
                      setCurrentPage(1);
                    }}
                    style={{
                      backgroundColor: '#F78D93',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginTop: '1rem'
                    }}
                  >
                    Filtreleri Temizle
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>👥</p>
                  <p>Henüz hiç kullanıcı kaydolmamış.</p>
                  <p style={{ fontSize: '0.9rem', color: '#999' }}>
                    Kullanıcılar Datezy uygulamasına kaydolduğunda burada görünecekler.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    Kullanıcı
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    E-posta
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    Yaş
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    Cinsiyet
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    Kayıt Tarihi
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    Durum
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'center', color: '#666', fontSize: '0.85rem', fontWeight: '600' }}>
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user, index) => (
                  <tr key={user.id} style={{ 
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                  }}>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Profil resmi gösterimi - state-based fallback */}
                        {(() => {
                          // Önce photos array'ini, sonra diğer alanları kontrol et
                          const profileImage = user.photos && user.photos.length > 0 
                            ? user.photos[0]
                            : user.photoURL || user.profilePictureUrl || user.profilePicUrl || user.googlePhotoURL || null;
                          
                          const hasImage = profileImage && !failedImages.has(user.id);
                          
                          return hasImage ? (
                            <img
                              src={profileImage}
                              alt={user.name || 'Kullanıcı'}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                marginRight: '1rem',
                                border: '2px solid #F78D93'
                              }}
                              onError={() => {
                                // Fotoğraf yüklenemezse state'e ekle - fallback gösterilecek
                                setFailedImages(prev => new Set(prev).add(user.id));
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: '#F78D93',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '1rem',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: 'white'
                            }}>
                              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </div>
                          );
                        })()}
                        <div>
                          <p style={{ margin: '0 0 0.25rem 0', fontWeight: '500', color: '#2c3e50' }}>
                            {user.name || 'İsimsiz Kullanıcı'}
                          </p>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>
                            📸 {user.photos?.length || 0} fotoğraf
                            {(user.photoURL || user.profilePictureUrl || user.profilePicUrl || user.googlePhotoURL) && ' + profil resmi'}
                            • ID: {user.id.substring(0, 8)}...
                          </p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: '#666' }}>
                      {user.email || 'E-posta yok'}
                    </td>
                    <td style={{ padding: '1rem', color: '#666' }}>
                      {user.age || 'Bilinmiyor'}
                    </td>
                    <td style={{ padding: '1rem', color: '#666' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {(() => {
                          const userGender = user.gender;
                          if (userGender === 'Erkek' || userGender === 'male') {
                            return (
                              <>
                                <span style={{ color: '#2196f3', fontSize: '1.1rem' }}>👨</span>
                                <span style={{ color: '#2196f3', fontWeight: '500' }}>Erkek</span>
                              </>
                            );
                          } else if (userGender === 'Kadın' || userGender === 'female') {
                            return (
                              <>
                                <span style={{ color: '#e91e63', fontSize: '1.1rem' }}>👩</span>
                                <span style={{ color: '#e91e63', fontWeight: '500' }}>Kadın</span>
                              </>
                            );
                          } else if (userGender === 'Diğer' || userGender === 'other') {
                            return (
                              <>
                                <span style={{ color: '#9c27b0', fontSize: '1.1rem' }}>🏳️‍⚧️</span>
                                <span style={{ color: '#9c27b0', fontWeight: '500' }}>Diğer</span>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <span style={{ color: '#999', fontSize: '1.1rem' }}>❓</span>
                                <span style={{ color: '#999', fontStyle: 'italic' }}>Belirtilmemiş</span>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: '#666' }}>
                      {formatDate(user.createdAt)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        backgroundColor: getUserStatus(user) === 'Aktif' ? '#27ae60' : '#e74c3c',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        {getUserStatus(user)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button 
                          title="Kullanıcı Detayları"
                          onClick={() => navigate(`/users/${user.id}`)}
                          style={{
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          👁️
                        </button>
                        <button 
                          title="Profil Düzenle"
                          onClick={() => alert('Profil düzenleme özelliği yakında eklenecek!')}
                          style={{
                            backgroundColor: '#95a5a6',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ✏️
                        </button>
                        <button 
                          title={user.isActive ? 'Kullanıcıyı Pasifleştir' : 'Kullanıcıyı Aktifleştir'}
                          onClick={() => handleUserStatusToggle(user.id, user.isActive)}
                          disabled={processingUserId === user.id}
                          style={{
                            backgroundColor: user.isActive ? '#e74c3c' : '#27ae60',
                            color: 'white',
                            border: 'none',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            cursor: processingUserId === user.id ? 'not-allowed' : 'pointer',
                            fontSize: '0.8rem',
                            opacity: processingUserId === user.id ? 0.6 : 1
                          }}
                        >
                          {processingUserId === user.id ? '⏳' : (user.isActive ? '🚫' : '✅')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {paginatedUsers.length > 0 && (
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fafafa'
          }}>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>
              {startIndex + 1}-{Math.min(startIndex + usersPerPage, filteredUsers.length)} / {filteredUsers.length} kullanıcı
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                ← Önceki
              </button>
              <span style={{ 
                padding: '0.5rem 1rem', 
                fontSize: '0.85rem',
                color: '#666'
              }}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSS Animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default UsersPage; 